var MyWebComponents = (function (t) {
  "use strict";
  /**
   * @license
   * Copyright 2019 Google LLC
   * SPDX-License-Identifier: BSD-3-Clause
   */ const e = globalThis,
    i =
      e.ShadowRoot &&
      (void 0 === e.ShadyCSS || e.ShadyCSS.nativeShadow) &&
      "adoptedStyleSheets" in Document.prototype &&
      "replace" in CSSStyleSheet.prototype,
    s = Symbol(),
    o = new WeakMap();
  class n {
    constructor(t, e, i) {
      if (((this._$cssResult$ = !0), i !== s))
        throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
      (this.cssText = t), (this.t = e);
    }
    get styleSheet() {
      let t = this.o;
      const e = this.t;
      if (i && void 0 === t) {
        const i = void 0 !== e && 1 === e.length;
        i && (t = o.get(e)),
          void 0 === t && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), i && o.set(e, t));
      }
      return t;
    }
    toString() {
      return this.cssText;
    }
  }
  const r = (t, ...e) => {
      const i =
        1 === t.length
          ? t[0]
          : e.reduce(
              (e, i, s) =>
                e +
                ((t) => {
                  if (!0 === t._$cssResult$) return t.cssText;
                  if ("number" == typeof t) return t;
                  throw Error(
                    "Value passed to 'css' function must be a 'css' function result: " +
                      t +
                      ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security."
                  );
                })(i) +
                t[s + 1],
              t[0]
            );
      return new n(i, t, s);
    },
    a = i
      ? (t) => t
      : (t) =>
          t instanceof CSSStyleSheet
            ? ((t) => {
                let e = "";
                for (const i of t.cssRules) e += i.cssText;
                return ((t) => new n("string" == typeof t ? t : t + "", void 0, s))(e);
              })(t)
            : t,
    /**
     * @license
     * Copyright 2017 Google LLC
     * SPDX-License-Identifier: BSD-3-Clause
     */ {
      is: l,
      defineProperty: c,
      getOwnPropertyDescriptor: h,
      getOwnPropertyNames: p,
      getOwnPropertySymbols: d,
      getPrototypeOf: u,
    } = Object,
    m = globalThis,
    g = m.trustedTypes,
    _ = g ? g.emptyScript : "",
    f = m.reactiveElementPolyfillSupport,
    $ = (t, e) => t,
    y = {
      toAttribute(t, e) {
        switch (e) {
          case Boolean:
            t = t ? _ : null;
            break;
          case Object:
          case Array:
            t = null == t ? t : JSON.stringify(t);
        }
        return t;
      },
      fromAttribute(t, e) {
        let i = t;
        switch (e) {
          case Boolean:
            i = null !== t;
            break;
          case Number:
            i = null === t ? null : Number(t);
            break;
          case Object:
          case Array:
            try {
              i = JSON.parse(t);
            } catch (t) {
              i = null;
            }
        }
        return i;
      },
    },
    b = (t, e) => !l(t, e),
    x = { attribute: !0, type: String, converter: y, reflect: !1, hasChanged: b };
  (Symbol.metadata ??= Symbol("metadata")), (m.litPropertyMetadata ??= new WeakMap());
  class v extends HTMLElement {
    static addInitializer(t) {
      this._$Ei(), (this.l ??= []).push(t);
    }
    static get observedAttributes() {
      return this.finalize(), this._$Eh && [...this._$Eh.keys()];
    }
    static createProperty(t, e = x) {
      if ((e.state && (e.attribute = !1), this._$Ei(), this.elementProperties.set(t, e), !e.noAccessor)) {
        const i = Symbol(),
          s = this.getPropertyDescriptor(t, i, e);
        void 0 !== s && c(this.prototype, t, s);
      }
    }
    static getPropertyDescriptor(t, e, i) {
      const { get: s, set: o } = h(this.prototype, t) ?? {
        get() {
          return this[e];
        },
        set(t) {
          this[e] = t;
        },
      };
      return {
        get() {
          return s?.call(this);
        },
        set(e) {
          const n = s?.call(this);
          o.call(this, e), this.requestUpdate(t, n, i);
        },
        configurable: !0,
        enumerable: !0,
      };
    }
    static getPropertyOptions(t) {
      return this.elementProperties.get(t) ?? x;
    }
    static _$Ei() {
      if (this.hasOwnProperty($("elementProperties"))) return;
      const t = u(this);
      t.finalize(), void 0 !== t.l && (this.l = [...t.l]), (this.elementProperties = new Map(t.elementProperties));
    }
    static finalize() {
      if (this.hasOwnProperty($("finalized"))) return;
      if (((this.finalized = !0), this._$Ei(), this.hasOwnProperty($("properties")))) {
        const t = this.properties,
          e = [...p(t), ...d(t)];
        for (const i of e) this.createProperty(i, t[i]);
      }
      const t = this[Symbol.metadata];
      if (null !== t) {
        const e = litPropertyMetadata.get(t);
        if (void 0 !== e) for (const [t, i] of e) this.elementProperties.set(t, i);
      }
      this._$Eh = new Map();
      for (const [t, e] of this.elementProperties) {
        const i = this._$Eu(t, e);
        void 0 !== i && this._$Eh.set(i, t);
      }
      this.elementStyles = this.finalizeStyles(this.styles);
    }
    static finalizeStyles(t) {
      const e = [];
      if (Array.isArray(t)) {
        const i = new Set(t.flat(1 / 0).reverse());
        for (const t of i) e.unshift(a(t));
      } else void 0 !== t && e.push(a(t));
      return e;
    }
    static _$Eu(t, e) {
      const i = e.attribute;
      return !1 === i ? void 0 : "string" == typeof i ? i : "string" == typeof t ? t.toLowerCase() : void 0;
    }
    constructor() {
      super(),
        (this._$Ep = void 0),
        (this.isUpdatePending = !1),
        (this.hasUpdated = !1),
        (this._$Em = null),
        this._$Ev();
    }
    _$Ev() {
      (this._$ES = new Promise((t) => (this.enableUpdating = t))),
        (this._$AL = new Map()),
        this._$E_(),
        this.requestUpdate(),
        this.constructor.l?.forEach((t) => t(this));
    }
    addController(t) {
      (this._$EO ??= new Set()).add(t), void 0 !== this.renderRoot && this.isConnected && t.hostConnected?.();
    }
    removeController(t) {
      this._$EO?.delete(t);
    }
    _$E_() {
      const t = new Map(),
        e = this.constructor.elementProperties;
      for (const i of e.keys()) this.hasOwnProperty(i) && (t.set(i, this[i]), delete this[i]);
      t.size > 0 && (this._$Ep = t);
    }
    createRenderRoot() {
      const t = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
      return (
        ((t, s) => {
          if (i) t.adoptedStyleSheets = s.map((t) => (t instanceof CSSStyleSheet ? t : t.styleSheet));
          else
            for (const i of s) {
              const s = document.createElement("style"),
                o = e.litNonce;
              void 0 !== o && s.setAttribute("nonce", o), (s.textContent = i.cssText), t.appendChild(s);
            }
        })(t, this.constructor.elementStyles),
        t
      );
    }
    connectedCallback() {
      (this.renderRoot ??= this.createRenderRoot()),
        this.enableUpdating(!0),
        this._$EO?.forEach((t) => t.hostConnected?.());
    }
    enableUpdating(t) {}
    disconnectedCallback() {
      this._$EO?.forEach((t) => t.hostDisconnected?.());
    }
    attributeChangedCallback(t, e, i) {
      this._$AK(t, i);
    }
    _$EC(t, e) {
      const i = this.constructor.elementProperties.get(t),
        s = this.constructor._$Eu(t, i);
      if (void 0 !== s && !0 === i.reflect) {
        const o = (void 0 !== i.converter?.toAttribute ? i.converter : y).toAttribute(e, i.type);
        (this._$Em = t), null == o ? this.removeAttribute(s) : this.setAttribute(s, o), (this._$Em = null);
      }
    }
    _$AK(t, e) {
      const i = this.constructor,
        s = i._$Eh.get(t);
      if (void 0 !== s && this._$Em !== s) {
        const t = i.getPropertyOptions(s),
          o =
            "function" == typeof t.converter
              ? { fromAttribute: t.converter }
              : void 0 !== t.converter?.fromAttribute
              ? t.converter
              : y;
        (this._$Em = s), (this[s] = o.fromAttribute(e, t.type)), (this._$Em = null);
      }
    }
    requestUpdate(t, e, i) {
      if (void 0 !== t) {
        if (((i ??= this.constructor.getPropertyOptions(t)), !(i.hasChanged ?? b)(this[t], e))) return;
        this.P(t, e, i);
      }
      !1 === this.isUpdatePending && (this._$ES = this._$ET());
    }
    P(t, e, i) {
      this._$AL.has(t) || this._$AL.set(t, e), !0 === i.reflect && this._$Em !== t && (this._$Ej ??= new Set()).add(t);
    }
    async _$ET() {
      this.isUpdatePending = !0;
      try {
        await this._$ES;
      } catch (t) {
        Promise.reject(t);
      }
      const t = this.scheduleUpdate();
      return null != t && (await t), !this.isUpdatePending;
    }
    scheduleUpdate() {
      return this.performUpdate();
    }
    performUpdate() {
      if (!this.isUpdatePending) return;
      if (!this.hasUpdated) {
        if (((this.renderRoot ??= this.createRenderRoot()), this._$Ep)) {
          for (const [t, e] of this._$Ep) this[t] = e;
          this._$Ep = void 0;
        }
        const t = this.constructor.elementProperties;
        if (t.size > 0)
          for (const [e, i] of t) !0 !== i.wrapped || this._$AL.has(e) || void 0 === this[e] || this.P(e, this[e], i);
      }
      let t = !1;
      const e = this._$AL;
      try {
        (t = this.shouldUpdate(e)),
          t ? (this.willUpdate(e), this._$EO?.forEach((t) => t.hostUpdate?.()), this.update(e)) : this._$EU();
      } catch (e) {
        throw ((t = !1), this._$EU(), e);
      }
      t && this._$AE(e);
    }
    willUpdate(t) {}
    _$AE(t) {
      this._$EO?.forEach((t) => t.hostUpdated?.()),
        this.hasUpdated || ((this.hasUpdated = !0), this.firstUpdated(t)),
        this.updated(t);
    }
    _$EU() {
      (this._$AL = new Map()), (this.isUpdatePending = !1);
    }
    get updateComplete() {
      return this.getUpdateComplete();
    }
    getUpdateComplete() {
      return this._$ES;
    }
    shouldUpdate(t) {
      return !0;
    }
    update(t) {
      (this._$Ej &&= this._$Ej.forEach((t) => this._$EC(t, this[t]))), this._$EU();
    }
    updated(t) {}
    firstUpdated(t) {}
  }
  (v.elementStyles = []),
    (v.shadowRootOptions = { mode: "open" }),
    (v[$("elementProperties")] = new Map()),
    (v[$("finalized")] = new Map()),
    f?.({ ReactiveElement: v }),
    (m.reactiveElementVersions ??= []).push("2.0.4");
  /**
   * @license
   * Copyright 2017 Google LLC
   * SPDX-License-Identifier: BSD-3-Clause
   */
  const A = globalThis,
    S = A.trustedTypes,
    w = S ? S.createPolicy("lit-html", { createHTML: (t) => t }) : void 0,
    E = "$lit$",
    k = `lit$${Math.random().toFixed(9).slice(2)}$`,
    T = "?" + k,
    C = `<${T}>`,
    U = document,
    P = () => U.createComment(""),
    N = (t) => null === t || ("object" != typeof t && "function" != typeof t),
    R = Array.isArray,
    z = "[ \t\n\f\r]",
    H = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g,
    O = /-->/g,
    M = />/g,
    L = RegExp(`>|${z}(?:([^\\s"'>=/]+)(${z}*=${z}*(?:[^ \t\n\f\r"'\`<>=]|("|')|))|$)`, "g"),
    I = /'/g,
    j = /"/g,
    D = /^(?:script|style|textarea|title)$/i,
    B = (
      (t) =>
      (e, ...i) => ({ _$litType$: t, strings: e, values: i })
    )(1),
    q = Symbol.for("lit-noChange"),
    W = Symbol.for("lit-nothing"),
    V = new WeakMap(),
    G = U.createTreeWalker(U, 129);
  function F(t, e) {
    if (!Array.isArray(t) || !t.hasOwnProperty("raw")) throw Error("invalid template strings array");
    return void 0 !== w ? w.createHTML(e) : e;
  }
  const K = (t, e) => {
    const i = t.length - 1,
      s = [];
    let o,
      n = 2 === e ? "<svg>" : "",
      r = H;
    for (let e = 0; e < i; e++) {
      const i = t[e];
      let a,
        l,
        c = -1,
        h = 0;
      for (; h < i.length && ((r.lastIndex = h), (l = r.exec(i)), null !== l); )
        (h = r.lastIndex),
          r === H
            ? "!--" === l[1]
              ? (r = O)
              : void 0 !== l[1]
              ? (r = M)
              : void 0 !== l[2]
              ? (D.test(l[2]) && (o = RegExp("</" + l[2], "g")), (r = L))
              : void 0 !== l[3] && (r = L)
            : r === L
            ? ">" === l[0]
              ? ((r = o ?? H), (c = -1))
              : void 0 === l[1]
              ? (c = -2)
              : ((c = r.lastIndex - l[2].length), (a = l[1]), (r = void 0 === l[3] ? L : '"' === l[3] ? j : I))
            : r === j || r === I
            ? (r = L)
            : r === O || r === M
            ? (r = H)
            : ((r = L), (o = void 0));
      const p = r === L && t[e + 1].startsWith("/>") ? " " : "";
      n += r === H ? i + C : c >= 0 ? (s.push(a), i.slice(0, c) + E + i.slice(c) + k + p) : i + k + (-2 === c ? e : p);
    }
    return [F(t, n + (t[i] || "<?>") + (2 === e ? "</svg>" : "")), s];
  };
  class J {
    constructor({ strings: t, _$litType$: e }, i) {
      let s;
      this.parts = [];
      let o = 0,
        n = 0;
      const r = t.length - 1,
        a = this.parts,
        [l, c] = K(t, e);
      if (((this.el = J.createElement(l, i)), (G.currentNode = this.el.content), 2 === e)) {
        const t = this.el.content.firstChild;
        t.replaceWith(...t.childNodes);
      }
      for (; null !== (s = G.nextNode()) && a.length < r; ) {
        if (1 === s.nodeType) {
          if (s.hasAttributes())
            for (const t of s.getAttributeNames())
              if (t.endsWith(E)) {
                const e = c[n++],
                  i = s.getAttribute(t).split(k),
                  r = /([.?@])?(.*)/.exec(e);
                a.push({
                  type: 1,
                  index: o,
                  name: r[2],
                  strings: i,
                  ctor: "." === r[1] ? tt : "?" === r[1] ? et : "@" === r[1] ? it : Y,
                }),
                  s.removeAttribute(t);
              } else t.startsWith(k) && (a.push({ type: 6, index: o }), s.removeAttribute(t));
          if (D.test(s.tagName)) {
            const t = s.textContent.split(k),
              e = t.length - 1;
            if (e > 0) {
              s.textContent = S ? S.emptyScript : "";
              for (let i = 0; i < e; i++) s.append(t[i], P()), G.nextNode(), a.push({ type: 2, index: ++o });
              s.append(t[e], P());
            }
          }
        } else if (8 === s.nodeType)
          if (s.data === T) a.push({ type: 2, index: o });
          else {
            let t = -1;
            for (; -1 !== (t = s.data.indexOf(k, t + 1)); ) a.push({ type: 7, index: o }), (t += k.length - 1);
          }
        o++;
      }
    }
    static createElement(t, e) {
      const i = U.createElement("template");
      return (i.innerHTML = t), i;
    }
  }
  function X(t, e, i = t, s) {
    if (e === q) return e;
    let o = void 0 !== s ? i._$Co?.[s] : i._$Cl;
    const n = N(e) ? void 0 : e._$litDirective$;
    return (
      o?.constructor !== n &&
        (o?._$AO?.(!1),
        void 0 === n ? (o = void 0) : ((o = new n(t)), o._$AT(t, i, s)),
        void 0 !== s ? ((i._$Co ??= [])[s] = o) : (i._$Cl = o)),
      void 0 !== o && (e = X(t, o._$AS(t, e.values), o, s)),
      e
    );
  }
  class Z {
    constructor(t, e) {
      (this._$AV = []), (this._$AN = void 0), (this._$AD = t), (this._$AM = e);
    }
    get parentNode() {
      return this._$AM.parentNode;
    }
    get _$AU() {
      return this._$AM._$AU;
    }
    u(t) {
      const {
          el: { content: e },
          parts: i,
        } = this._$AD,
        s = (t?.creationScope ?? U).importNode(e, !0);
      G.currentNode = s;
      let o = G.nextNode(),
        n = 0,
        r = 0,
        a = i[0];
      for (; void 0 !== a; ) {
        if (n === a.index) {
          let e;
          2 === a.type
            ? (e = new Q(o, o.nextSibling, this, t))
            : 1 === a.type
            ? (e = new a.ctor(o, a.name, a.strings, this, t))
            : 6 === a.type && (e = new st(o, this, t)),
            this._$AV.push(e),
            (a = i[++r]);
        }
        n !== a?.index && ((o = G.nextNode()), n++);
      }
      return (G.currentNode = U), s;
    }
    p(t) {
      let e = 0;
      for (const i of this._$AV)
        void 0 !== i && (void 0 !== i.strings ? (i._$AI(t, i, e), (e += i.strings.length - 2)) : i._$AI(t[e])), e++;
    }
  }
  class Q {
    get _$AU() {
      return this._$AM?._$AU ?? this._$Cv;
    }
    constructor(t, e, i, s) {
      (this.type = 2),
        (this._$AH = W),
        (this._$AN = void 0),
        (this._$AA = t),
        (this._$AB = e),
        (this._$AM = i),
        (this.options = s),
        (this._$Cv = s?.isConnected ?? !0);
    }
    get parentNode() {
      let t = this._$AA.parentNode;
      const e = this._$AM;
      return void 0 !== e && 11 === t?.nodeType && (t = e.parentNode), t;
    }
    get startNode() {
      return this._$AA;
    }
    get endNode() {
      return this._$AB;
    }
    _$AI(t, e = this) {
      (t = X(this, t, e)),
        N(t)
          ? t === W || null == t || "" === t
            ? (this._$AH !== W && this._$AR(), (this._$AH = W))
            : t !== this._$AH && t !== q && this._(t)
          : void 0 !== t._$litType$
          ? this.$(t)
          : void 0 !== t.nodeType
          ? this.T(t)
          : ((t) => R(t) || "function" == typeof t?.[Symbol.iterator])(t)
          ? this.k(t)
          : this._(t);
    }
    S(t) {
      return this._$AA.parentNode.insertBefore(t, this._$AB);
    }
    T(t) {
      this._$AH !== t && (this._$AR(), (this._$AH = this.S(t)));
    }
    _(t) {
      this._$AH !== W && N(this._$AH) ? (this._$AA.nextSibling.data = t) : this.T(U.createTextNode(t)), (this._$AH = t);
    }
    $(t) {
      const { values: e, _$litType$: i } = t,
        s =
          "number" == typeof i
            ? this._$AC(t)
            : (void 0 === i.el && (i.el = J.createElement(F(i.h, i.h[0]), this.options)), i);
      if (this._$AH?._$AD === s) this._$AH.p(e);
      else {
        const t = new Z(s, this),
          i = t.u(this.options);
        t.p(e), this.T(i), (this._$AH = t);
      }
    }
    _$AC(t) {
      let e = V.get(t.strings);
      return void 0 === e && V.set(t.strings, (e = new J(t))), e;
    }
    k(t) {
      R(this._$AH) || ((this._$AH = []), this._$AR());
      const e = this._$AH;
      let i,
        s = 0;
      for (const o of t)
        s === e.length ? e.push((i = new Q(this.S(P()), this.S(P()), this, this.options))) : (i = e[s]), i._$AI(o), s++;
      s < e.length && (this._$AR(i && i._$AB.nextSibling, s), (e.length = s));
    }
    _$AR(t = this._$AA.nextSibling, e) {
      for (this._$AP?.(!1, !0, e); t && t !== this._$AB; ) {
        const e = t.nextSibling;
        t.remove(), (t = e);
      }
    }
    setConnected(t) {
      void 0 === this._$AM && ((this._$Cv = t), this._$AP?.(t));
    }
  }
  class Y {
    get tagName() {
      return this.element.tagName;
    }
    get _$AU() {
      return this._$AM._$AU;
    }
    constructor(t, e, i, s, o) {
      (this.type = 1),
        (this._$AH = W),
        (this._$AN = void 0),
        (this.element = t),
        (this.name = e),
        (this._$AM = s),
        (this.options = o),
        i.length > 2 || "" !== i[0] || "" !== i[1]
          ? ((this._$AH = Array(i.length - 1).fill(new String())), (this.strings = i))
          : (this._$AH = W);
    }
    _$AI(t, e = this, i, s) {
      const o = this.strings;
      let n = !1;
      if (void 0 === o) (t = X(this, t, e, 0)), (n = !N(t) || (t !== this._$AH && t !== q)), n && (this._$AH = t);
      else {
        const s = t;
        let r, a;
        for (t = o[0], r = 0; r < o.length - 1; r++)
          (a = X(this, s[i + r], e, r)),
            a === q && (a = this._$AH[r]),
            (n ||= !N(a) || a !== this._$AH[r]),
            a === W ? (t = W) : t !== W && (t += (a ?? "") + o[r + 1]),
            (this._$AH[r] = a);
      }
      n && !s && this.j(t);
    }
    j(t) {
      t === W ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
    }
  }
  class tt extends Y {
    constructor() {
      super(...arguments), (this.type = 3);
    }
    j(t) {
      this.element[this.name] = t === W ? void 0 : t;
    }
  }
  class et extends Y {
    constructor() {
      super(...arguments), (this.type = 4);
    }
    j(t) {
      this.element.toggleAttribute(this.name, !!t && t !== W);
    }
  }
  class it extends Y {
    constructor(t, e, i, s, o) {
      super(t, e, i, s, o), (this.type = 5);
    }
    _$AI(t, e = this) {
      if ((t = X(this, t, e, 0) ?? W) === q) return;
      const i = this._$AH,
        s = (t === W && i !== W) || t.capture !== i.capture || t.once !== i.once || t.passive !== i.passive,
        o = t !== W && (i === W || s);
      s && this.element.removeEventListener(this.name, this, i),
        o && this.element.addEventListener(this.name, this, t),
        (this._$AH = t);
    }
    handleEvent(t) {
      "function" == typeof this._$AH ? this._$AH.call(this.options?.host ?? this.element, t) : this._$AH.handleEvent(t);
    }
  }
  class st {
    constructor(t, e, i) {
      (this.element = t), (this.type = 6), (this._$AN = void 0), (this._$AM = e), (this.options = i);
    }
    get _$AU() {
      return this._$AM._$AU;
    }
    _$AI(t) {
      X(this, t);
    }
  }
  const ot = A.litHtmlPolyfillSupport;
  ot?.(J, Q), (A.litHtmlVersions ??= []).push("3.1.4");
  /**
   * @license
   * Copyright 2017 Google LLC
   * SPDX-License-Identifier: BSD-3-Clause
   */
  class nt extends v {
    constructor() {
      super(...arguments), (this.renderOptions = { host: this }), (this._$Do = void 0);
    }
    createRenderRoot() {
      const t = super.createRenderRoot();
      return (this.renderOptions.renderBefore ??= t.firstChild), t;
    }
    update(t) {
      const e = this.render();
      this.hasUpdated || (this.renderOptions.isConnected = this.isConnected),
        super.update(t),
        (this._$Do = ((t, e, i) => {
          const s = i?.renderBefore ?? e;
          let o = s._$litPart$;
          if (void 0 === o) {
            const t = i?.renderBefore ?? null;
            s._$litPart$ = o = new Q(e.insertBefore(P(), t), t, void 0, i ?? {});
          }
          return o._$AI(t), o;
        })(e, this.renderRoot, this.renderOptions));
    }
    connectedCallback() {
      super.connectedCallback(), this._$Do?.setConnected(!0);
    }
    disconnectedCallback() {
      super.disconnectedCallback(), this._$Do?.setConnected(!1);
    }
    render() {
      return q;
    }
  }
  (nt._$litElement$ = !0), (nt.finalized = !0), globalThis.litElementHydrateSupport?.({ LitElement: nt });
  const rt = globalThis.litElementPolyfillSupport;
  rt?.({ LitElement: nt }), (globalThis.litElementVersions ??= []).push("4.0.6");
  class at extends nt {
    static properties = {
      backgroundColor: { type: String, attribute: "background-color" },
      color: { type: String },
      messages: { type: String },
      icons: { type: String },
    };
    static styles = r`
      .marquee {
        --gap: 8rem;
        display: flex;
        overflow: hidden;
        user-select: none;
        gap: var(--gap);
        height: 25px;
        background-color: var(--background-color, black);
        color: var(--color, white);
      }
  
      .marquee__content {
        flex-shrink: 0;
        display: flex;
        justify-content: space-around;
        min-width: 100%;
        gap: var(--gap);
        animation: scroll 10s linear infinite;
        margin: 0;
        padding: 0;
        list-style: none;
        align-items: center;
        font-size: 10px;
      }
  
      .marquee__item {
        display: flex;
        align-items: center;
        gap: 5px;
      }
  
      @keyframes scroll {
        from {
          transform: translateX(0);
        }
        to {
          transform: translateX(calc(-100% - var(--gap)));
        }
      }
  
      @media screen and (min-width: 1024px) {
        .marquee {
          height: 40px;
        }
  
        .marquee__content {
          font-size: 16px;
          animation: none;
        }
      }
    `;
    constructor() {
      super(),
        (this.backgroundColor = "black"),
        (this.color = "white"),
        (this.messages = "Item 1, Item 2, Item 3"),
        (this.icons = "");
    }
    render() {
      const t = this.messages.split(","),
        e = this.icons.split(",");
      return B`
        <style>
          :host {
            --background-color: ${this.backgroundColor};
            --color: ${this.color};
          }
        </style>
        <div class="marquee">
          <ul class="marquee__content">
            ${t.map(
              (t, i) => B`
                <li class="marquee__item">
                  ${e[i] ? B`<img src="${e[i].trim()}" alt="icon" />` : ""}
                  ${t.trim()}
                </li>
              `
            )}
          </ul>
          <ul class="marquee__content" aria-hidden="true">
            ${t.map(
              (t, i) => B`
                <li class="marquee__item">
                  ${e[i] ? B`<img src="${e[i].trim()}" alt="icon" />` : ""}
                  ${t.trim()}
                </li>
              `
            )}
          </ul>
        </div>
      `;
    }
  }
  customElements.define("marquee-component", at);
  class lt extends nt {
    static properties = {
      tip: { type: String },
      title: { type: String },
      titleFont: { type: String, attribute: "title-font" },
      subtitle: { type: String },
      buttonText: { type: String, attribute: "button-text" },
      buttonLink: { type: String, attribute: "button-link" },
      backgroundImage: { type: String, attribute: "background-image" },
    };
    static styles = r`
      @import url("https://fonts.googleapis.com/css2?family=Teko:wght@300..700&display=swap");
      @import url("https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap");
  
      * {
        box-sizing: border-box;
      }
  
      .hero {
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        height: 320px;
        width: 100%;
        font-size: 12px;
        color: white;
        font-family: "Roboto", sans-serif;
        display: flex;
        flex-direction: column;
      }
  
      .hero__content {
        background-color: linear-gradient(
          0deg,
          rgba(0, 0, 0, 0.4) 0%,
          rgba(0, 0, 0, 0.4) 100%
        );
        padding-inline: 24px;
        padding-top: 66px;
        width: 100%;
        height: 100%;
      }
  
      .hero__tip {
        display: flex;
        padding: 6px 22px;
        justify-content: center;
        align-items: center;
        border: 2px solid #dbd834;
        color: #dbd834;
        text-transform: uppercase;
      }
  
      .hero__title {
        font-size: 32px;
        font-weight: bold;
        text-transform: uppercase;
        line-height: 1;
      }
  
      .hero__subtitle {
        font-size: 20px;
      }
  
      .hero__button {
        display: flex;
        justify-content: center;
        align-items: center;
        background: #748b46;
        padding: 7px 24px;
        color: white;
        text-decoration: none;
        text-transform: uppercase;
      }
  
      .hero__content-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
      }
  
      @media screen and (min-width: 1024px) {
        .hero {
          height: unset;
          background-color: linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.17) 0%,
            rgba(0, 0, 0, 0.17) 100%
          );
          font-size: 16px;
          flex-direction: column-reverse;
          align-items: center;
          justify-content: stretch;
        }
  
        .hero__content {
          max-width: 1400px;
          padding-inline: 0;
          padding-block: 86px;
          background: none;
        }
  
        .hero__content-container {
          max-width: 1012px;
          display: flex;
          flex-direction: column;
          gap: 38px;
          align-items: flex-start;
          padding-inline: 24px;
        }
  
        .hero__title {
          font-size: 72px;
        }
  
        .hero__subtitle {
          font-size: 36px;
        }
  
        .hero__button {
          padding: 13px 31px;
        }
      }
    `;
    constructor() {
      super(),
        (this.tip = "Tipp"),
        (this.title = "Performance auf höchstem Level"),
        (this.subtitle = "Sichere dir Original-Bekleidung und Gear der Bundeswehr!"),
        (this.buttonText = "Jetzt Entdecken"),
        (this.buttonLink = "#"),
        (this.backgroundImage = "");
    }
    render() {
      return B`
        <div class="hero" style="background-image: url(${this.backgroundImage})">
          <div class="hero__content">
            <div class="hero__content-container">
              <div class="hero__tip">${this.tip}</div>
              <div class="hero__title" style="font-family: ${this.titleFont}">
                ${this.title}
              </div>
              <div class="hero__subtitle">${this.subtitle}</div>
              <a class="hero__button" href="${this.buttonLink}"
                >${this.buttonText}</a
              >
            </div>
          </div>
          <marquee-component
            background-color="black"
            color="white"
            messages="Kauf auf Rechnung,Lieferung innerhalb von 24 Stunden*,BEST-PREIS-GARANTIE"
            style="width: 100vw"
          ></marquee-component>
        </div>
      `;
    }
  }
  customElements.define("hero-component", lt);
  class ct extends nt {
    static styles = r`
      @import url("https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap");
  
      * {
        box-sizing: border-box;
      }
  
      .button {
        display: flex;
        justify-content: center;
        align-items: center;
        background: #748b46;
        padding: 7px 24px;
        color: white;
        text-decoration: none;
        text-transform: uppercase;
        border: none;
        cursor: pointer;
        font-family: "Roboto", sans-serif;
        font-weight: bold;
      }
  
      @media (min-width: 1024px) {
        .button {
          padding: 13px 31px;
        }
      }
    `;
    render() {
      return B`
        <button class="button">
          <slot></slot>
        </button>
      `;
    }
  }
  customElements.define("button-component", ct);
  /**
   * @license
   * Copyright 2017 Google LLC
   * SPDX-License-Identifier: BSD-3-Clause
   */
  const ht = 2;
  class pt {
    constructor(t) {}
    get _$AU() {
      return this._$AM._$AU;
    }
    _$AT(t, e, i) {
      (this._$Ct = t), (this._$AM = e), (this._$Ci = i);
    }
    _$AS(t, e) {
      return this.update(t, e);
    }
    update(t, e) {
      return this.render(...e);
    }
  }
  /**
   * @license
   * Copyright 2017 Google LLC
   * SPDX-License-Identifier: BSD-3-Clause
   */ class dt extends pt {
    constructor(t) {
      if ((super(t), (this.it = W), t.type !== ht))
        throw Error(this.constructor.directiveName + "() can only be used in child bindings");
    }
    render(t) {
      if (t === W || null == t) return (this._t = void 0), (this.it = t);
      if (t === q) return t;
      if ("string" != typeof t) throw Error(this.constructor.directiveName + "() called with a non-string value");
      if (t === this.it) return this._t;
      this.it = t;
      const e = [t];
      return (e.raw = e), (this._t = { _$litType$: this.constructor.resultType, strings: e, values: [] });
    }
  }
  (dt.directiveName = "unsafeHTML"), (dt.resultType = 1);
  const ut = (
    (t) =>
    (...e) => ({ _$litDirective$: t, values: e })
  )(dt);
  class mt extends nt {
    static properties = {
      title: { type: String },
      description: { type: String },
      imageUrl: { type: String },
      imageUrl2: { type: String },
      title2: { type: String },
      buttonText: { type: String },
      sectionTitle: { type: String },
      promoText: { type: String },
      promoImageUrl: { type: String },
      promoLogoUrl: { type: String },
      promoOfferText: { type: String },
      promoDescription: { type: String },
    };
    static styles = r`
      * {
        box-sizing: border-box;
      }
  
      @import url("https://fonts.googleapis.com/css2?family=Teko:wght@300..700&display=swap");
      @import url("https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900&display=swap");
  
      :host {
        display: block;
        box-sizing: border-box;
      }
  
      .promo {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 4px;
      }
  
      .promo__section {
        position: relative;
        width: 100%;
      }
  
      .promo__section--long {
        min-height: 229px;
      }
  
      .promo__image-wrapper {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-size: cover;
      }
  
      .promo__overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          0deg,
          rgba(0, 0, 0, 0.23) 0%,
          rgba(0, 0, 0, 0.23) 100%
        );
      }
  
      .promo__content {
        position: absolute;
        bottom: 0;
        left: 0;
        z-index: 1;
        width: 100%;
        padding: 18px;
      }
  
      .promo__title {
        font-family: "Teko", sans-serif;
        color: white;
        font-size: 20px;
        font-weight: bold;
        margin: 0;
      }
  
      .promo__split-section {
        display: flex;
        background-color: #5c594f;
        height: 107px;
      }
  
      .promo__image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        flex: 1;
        max-height: 300px;
      }
  
      .promo__text {
        font-family: "Teko", sans-serif;
        font-size: 20px;
        font-weight: bold;
        color: white;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding-inline: 19px;
        flex-direction: column;
        line-height: 1;
        gap: 11px;
        flex: 1;
      }
  
      .promo__text-image {
        width: 77px;
        height: auto;
      }
  
      .promo__background-section {
        background-color: #5c594f;
        color: white;
        padding: 20px;
      }
  
      .promo__offer-text {
        font-family: "Teko", sans-serif;
        font-size: 20px;
        font-weight: bold;
        line-height: 1;
        margin-bottom: 8px;
        margin-top: 0;
      }
  
      .promo__description {
        font-family: "Roboto", sans-serif;
        font-size: 14px;
        margin: 0;
        color: white;
      }
  
      .promo__section-3,
      .promo__section-4 {
        grid-column: span 2;
      }
  
      .desktop-only {
        display: none;
      }
  
      .mobile-only {
        display: block;
      }
  
      .section-title {
        display: none;
      }
  
      .container {
        max-width: 1400px;
        margin: 0 auto;
      }
  
      figure {
        margin: 0;
      }
  
      @media screen and (min-width: 1024px) {
        .promo {
          grid-template-rows: 1fr 1fr;
          grid-template-columns: 387px 1fr 1fr;
          row-gap: 20px;
          column-gap: 16px;
        }
  
        .promo__section-1 {
          grid-row: span 2;
        }
  
        .promo__section-2 {
          grid-column: span 2;
        }
  
        .promo__section-3,
        .promo__section-4 {
          grid-column: span 1;
        }
  
        .promo__description {
          font-size: 16px;
        }
  
        .desktop-only {
          display: block;
        }
  
        .promo__title {
          font-size: 32px;
          height: 32px;
        }
  
        .promo__content {
          display: flex;
          flex-direction: column;
          gap: 17px;
        }
  
        .promo__split-section {
          height: unset;
        }
  
        .mobile-only {
          display: none;
        }
  
        .promo__text {
          order: 1;
          font-size: 32px;
          padding-bottom: 38px;
          justify-content: flex-end;
          padding-left: 40px;
          padding-right: 42px;
        }
  
        .promo__image {
          order: 2;
        }
  
        .promo__offer-text {
          font-size: 32px;
        }
  
        .center--desktop {
          display: flex;
          justify-content: center;
          align-items: center;
        }
  
        .section-title {
          display: block;
          font-family: "Teko", sans-serif;
          font-size: 32px;
          font-weight: bold;
          margin-bottom: 39px;
          margin-top: 0;
          text-transform: uppercase;
          height: 31px;
          color: black;
        }
  
        .promo__section-4 {
          padding: 42px 44px 42px 48px;
        }
      }
    `;
    constructor() {
      super(),
        (this.title = "SALE"),
        (this.description =
          "Entdecke hochwertige Ausrüstung und Bekleidung zu unschlagbaren Preisen. Nur solange der Vorrat reicht – hol Dir Dein Schnäppchen!"),
        (this.imageUrl =
          "https://cdn.shopify.com/s/files/1/0864/3523/4098/files/e0496a7869bcbbad7de942f9c08f95b9.jpg?v=1718369177"),
        (this.buttonText = "Sale entdecken"),
        (this.sectionTitle = "asmc - dein bundeswehr und militärshop"),
        (this.promoText = "DAS BESTE VON GARMIN"),
        (this.promoImageUrl =
          "https://cdn.shopify.com/s/files/1/0864/3523/4098/files/3db1ce3bcb37d3290a384b481246439a.png?v=1718533783"),
        (this.promoLogoUrl =
          "https://cdn.shopify.com/s/files/1/0864/3523/4098/files/aa387ddce88627c535eb12d753a1eb84.png?v=1718535075"),
        (this.promoOfferText = "ANGEBOT INNERHALB VON 24 STUNDEN**"),
        (this.promoDescription =
          "Wenn Sie im Auftrag einer offiziellen Behörde handeln, bieten wir gerne die Erstellung eines individuellen Angebotes an. Rufen Sie einfach unsere Hotline unter <strong>+49 6821 9449-666</strong> an!"),
        (this.imageUrl2 =
          "https://cdn.shopify.com/s/files/1/0864/3523/4098/files/e0496a7869bcbbad7de942f9c08f95b9.jpg?v=1718369177"),
        (this.title2 = "FÜR KUNDEN");
    }
    render() {
      return B`
        <section class="container">
          <h2 class="section-title">${this.sectionTitle}</h2>
          <div class="promo">
            <article class="promo__section promo__section--long promo__section-1">
              <figure
                class="promo__image-wrapper"
                style="background-image: url(${this.imageUrl});"
              >
                <div class="promo__overlay"></div>
              </figure>
              <div class="promo__content">
                <h3 class="promo__title">${this.title}</h3>
                <p class="promo__description desktop-only">${this.description}</p>
                <button-component>${this.buttonText}</button-component>
              </div>
            </article>
            <article class="promo__section promo__section--long promo__section-2">
              <figure
                class="promo__image-wrapper"
                style="background-image: url(${this.imageUrl2});"
              >
                <div class="promo__overlay"></div>
              </figure>
              <div class="promo__content">
                <h3 class="promo__title mobile-only">${this.title2}</h3>
                <button-component class="mobile-only"
                  >${this.buttonText}</button-component
                >
              </div>
            </article>
            <article class="promo__section promo__section-3 promo__split-section">
              <img
                src=${this.promoImageUrl}
                alt="Garmin Watch"
                class="promo__image"
              />
              <div class="promo__text">
                <img
                  src=${this.promoLogoUrl}
                  alt="Logo"
                  class="promo__text-image"
                />
                <span>${this.promoText}</span>
              </div>
            </article>
            <article
              class="promo__section promo__section-4 promo__background-section center--desktop"
            >
              <div>
                <p class="promo__offer-text">${this.promoOfferText}</p>
                <p class="promo__description">${ut(this.promoDescription)}</p>
              </div>
            </article>
          </div>
        </section>
      `;
    }
  }
  return (
    customElements.define("promo-component", mt),
    (t.ButtonComponent = ct),
    (t.HeroComponent = lt),
    (t.MarqueeComponent = at),
    (t.PromoComponent = mt),
    Object.defineProperty(t, "__esModule", { value: !0 }),
    t
  );
})({});
//# sourceMappingURL=my-web-components.js.map
